import React, { useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  CloudUpload,
  Crosshair,
  Loader2,
  Pencil,
  Plus,
  Ruler,
  Search,
  Target as TargetIcon,
  Trash2,
} from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import MissingTargetNotice from "../../components/MissingTargetNotice";
import ConfirmDialog from "../../components/ConfirmDialog";
import { BulkBar, RowBox, SelectAllBox, useTableSelection } from "../../components/TableSelection";
import { Button, EmptyState, Loading, Modal, Select, StatCard } from "../../components/ui";
import { apiDelete, apiPost, apiUpload, useApi } from "../../api/client";
import { TargetFace, TargetFaceRing } from "../../data/types";

/*
 * Target definitions — the ring geometry every shot map is drawn from.
 *
 * A shot log records where a shot struck and the name of the face it struck
 * ("AU-600y"), but never how big the rings are. Those come from the target
 * system's export file, imported here and then held as portal data: they can be
 * edited, added to and removed like anything else, and re-importing the file
 * adds what is new and updates what has changed without disturbing the rest.
 *
 * Until a face is imported, results that use it still show in full — they are
 * simply plotted without scoring rings, which is flagged wherever they appear.
 */

/** Faces are grouped by their prefix — AU, US, SSAA, KRC — which is the family. */
const familyOf = (name: string) => {
  const head = String(name).split("-")[0];
  return head && head !== name ? head.toUpperCase() : "Other";
};

const ringSummary = (rings: TargetFaceRing[] = []) =>
  rings
    .filter((r) => r.shape === "ring")
    .map((r) => `${r.label} ${Math.round(r.diameterMm ?? (r.radiusMm ?? 0) * 2)}mm`)
    .join(" · ");

export default function TargetsPage() {
  const { data: faces, loading, reload } = useApi<TargetFace[]>("/target-faces", []);
  const { data: missing, reload: reloadMissing } = useApi<string[]>("/target-faces/missing", []);
  const [search, setSearch] = useState("");
  const [family, setFamily] = useState("All Types");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);
  // One code path for one target or many — a single removal is a list of one.
  const [pending, setPending] = useState<TargetFace[]>([]);
  const [editing, setEditing] = useState<TargetFace | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const families = useMemo(
    () => Array.from(new Set(faces.map((f) => familyOf(f.name)))).sort(),
    [faces]
  );

  const q = search.trim().toLowerCase();
  const filtered = faces.filter(
    (f) => (!q || f.name.toLowerCase().includes(q)) && (family === "All Types" || familyOf(f.name) === family)
  );
  // Selection follows the filtered list, so "select all" after choosing a type
  // targets just that family of targets.
  const selection = useTableSelection(filtered.map((f) => f.name));

  /* Grouped by family so a long list reads as "the AU targets", "the US targets". */
  const grouped = useMemo(() => {
    const map = new Map<string, TargetFace[]>();
    filtered.forEach((f) => {
      const k = familyOf(f.name);
      map.set(k, [...(map.get(k) || []), f]);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const refresh = () => {
    reload();
    reloadMissing();
  };

  const upload = async (file: File) => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await apiUpload<{ message: string }>("/admin/import/target-faces", file);
      setNotice({ tone: "ok", text: res.message });
      refresh();
    } catch (e) {
      setNotice({ tone: "bad", text: (e as Error).message });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    const failed: string[] = [];
    for (const f of pending) {
      try {
        await apiDelete<{ message: string }>(`/admin/target-faces/${encodeURIComponent(f.name)}`);
      } catch (e) {
        failed.push(`${f.name}: ${(e as Error).message}`);
      }
    }
    setBusy(false);
    setPending([]);
    selection.clear();
    refresh();
    setNotice(
      failed.length
        ? { tone: "bad", text: `${pending.length - failed.length} of ${pending.length} removed. ${failed[0]}` }
        : { tone: "ok", text: `${pending.length} target${pending.length === 1 ? "" : "s"} removed.` }
    );
  };

  return (
    <div>
      <AdminTopbar
        title="Targets"
        subtitle="Ring geometry for every target face. Import the target definition file, then add, edit or remove faces as needed."
        actions={
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            />
            <Button variant="secondary" onClick={() => setEditing({ name: "", rings: [] } as unknown as TargetFace)}>
              <Plus size={16} /> Add Target
            </Button>
            <Button onClick={() => fileRef.current?.click()} disabled={busy}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CloudUpload size={16} />} Import Definitions File
            </Button>
          </>
        }
      />

      {/*
        A drop zone as well as the button. The definitions could only be loaded
        by finding one blue button in the corner, which is not where anyone looks
        for "how do I load a file" — and dropping the file on the page, the
        obvious thing to try, did nothing at all.
      */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const f = e.dataTransfer.files?.[0];
          if (f) upload(f);
        }}
        className={`mb-3 flex items-center justify-center gap-3 rounded-xl border-2 border-dashed px-4 py-3 text-[13px] transition-colors ${
          dragging ? "border-primary bg-primary-soft/60 text-primary" : "border-slate-300 text-muted hover:border-primary/60"
        }`}
      >
        <CloudUpload size={18} className={dragging ? "text-primary" : "text-slate-400"} />
        <span>
          Drop the <b className="text-ink">target definition file</b> (.json) here, or{" "}
          <button className="font-bold text-primary hover:underline" onClick={() => fileRef.current?.click()} disabled={busy}>
            choose a file
          </button>
          . Loading it again adds new faces and updates existing ones.
        </span>
      </div>

      {notice && (
        <div
          className={`mb-3 flex items-start gap-2.5 rounded-xl border px-4 py-3 text-[13px] ${
            notice.tone === "ok" ? "border-success/30 bg-success-soft text-success" : "border-danger/30 bg-danger-soft text-danger"
          }`}
        >
          {notice.tone === "ok" ? <CheckCircle2 size={16} className="mt-px shrink-0" /> : <AlertCircle size={16} className="mt-px shrink-0" />}
          <span className="min-w-0 font-semibold">{notice.text}</span>
          <button className="ml-auto shrink-0 font-bold opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>
            ✕
          </button>
        </div>
      )}

      {/*
        The same notice the results pages show, so the wording an administrator
        reads here matches what a shooter sees on their target.
      */}
      <MissingTargetNotice faces={missing} storedCount={loading ? undefined : faces.length} className="mb-3" />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={<TargetIcon size={20} />} value={faces.length} label="Target Faces" sub="Stored" />
        <StatCard icon={<Ruler size={20} />} value={families.length} label="Target Types" iconBg="bg-violet-50 text-violet-600" />
        <StatCard
          icon={<Crosshair size={20} />}
          value={faces.filter((f) => (f.f?.length ?? 0) > 0).length}
          label="With F-Class Rings"
          iconBg="bg-success-soft text-success"
        />
        <StatCard
          icon={<AlertCircle size={20} />}
          value={missing.length}
          label="Missing Definitions"
          iconBg={missing.length ? "bg-warning-soft text-warning" : "bg-slate-100 text-muted"}
        />
      </div>

      <div className="card mt-3 flex flex-wrap items-end gap-3 p-3">
        <div className="min-w-[240px] flex-1">
          <label className="label">Search</label>
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="input pl-9" placeholder="Search target name..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">Target Type</label>
          <Select options={["All Types", ...families]} value={family} onChange={setFamily} className="w-full sm:w-44" />
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            setSearch("");
            setFamily("All Types");
          }}
        >
          Reset
        </Button>
      </div>

      <BulkBar
        count={selection.count}
        noun="target"
        onClear={selection.clear}
        onDelete={() => setPending(filtered.filter((f) => selection.isSelected(f.name)))}
      />

      <div className="card mt-3">
        <div className="table-scroll [--table-offset:526px]">
          <table className="w-full min-w-[1120px]">
            <thead>
              <tr className="border-b border-line bg-slate-50">
                <th className="th w-10">
                  <SelectAllBox
                    checked={selection.allSelected}
                    indeterminate={selection.someSelected}
                    onChange={selection.toggleAll}
                    label="Select all targets"
                  />
                </th>
                <th className="th">Target</th>
                <th className="th">Type</th>
                <th className="th text-right">Metres</th>
                <th className="th text-right">Yards</th>
                {/*
                  How many millimetres one minute of angle covers at this
                  distance. It is what turns a shot's millimetre position on the
                  target into the MOA figures on the shot map and in the group
                  statistics, so the heading says what it is rather than leaving
                  a reader to work out which way round the division goes.
                */}
                <th className="th text-right leading-tight" title="Millimetres covered by one minute of angle at this distance">
                  mm per MOA
                  <span className="block font-normal normal-case text-muted">at distance</span>
                </th>
                <th className="th text-right" title="The black of the target — what a shooter aims at">
                  Aiming Mark
                </th>
                <th className="th">TR Rings (diameter)</th>
                <th className="th text-center">F-Class</th>
                <th className="th text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {loading && (
                <tr>
                  <td colSpan={11} className="td py-10 text-center text-muted">
                    <Loading />
                  </td>
                </tr>
              )}
              {!loading && !filtered.length && (
                <tr>
                  <td colSpan={11} className="td py-12 text-center">
                    <EmptyState
                      title={faces.length ? "No targets match the filters" : "No target definitions yet"}
                      message={
                        faces.length
                          ? "Clear the search or choose a different type."
                          : "Import the target definition file to draw shot maps on their correct rings."
                      }
                    />
                  </td>
                </tr>
              )}
              {grouped.map(([name, members]) => (
                <React.Fragment key={name}>
                  <tr className="bg-slate-50/80">
                    <td colSpan={11} className="px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-muted">
                      {name} — {members.length} target{members.length === 1 ? "" : "s"}
                    </td>
                  </tr>
                  {members.map((f) => (
                    <tr
                      key={f.name}
                      className={`transition-colors ${selection.isSelected(f.name) ? "bg-primary-soft/50" : "hover:bg-primary-soft/30"}`}
                    >
                      <td className="td">
                        <RowBox checked={selection.isSelected(f.name)} onChange={() => selection.toggle(f.name)} label={`Select ${f.name}`} />
                      </td>
                      <td className="td font-bold text-ink">{f.name}</td>
                      <td className="td text-muted">{familyOf(f.name)}</td>
                      {/*
                        A face is defined in metres, but half the programme is
                        shot in yards — "AU-600y" at 549 m is 600 yards, and an
                        administrator checking a range card should not have to
                        do the conversion in their head.
                      */}
                      <td className="td text-right">{f.distanceM ? `${Math.round(f.distanceM)} m` : "—"}</td>
                      <td className="td text-right text-muted">{f.distanceM ? `${Math.round(f.distanceM / 0.9144)} y` : "—"}</td>
                      <td className="td text-right text-muted">{f.mmPerMoa ? f.mmPerMoa.toFixed(1) : "—"}</td>
                      {/* Figure targets carry no black, so they have none. */}
                      <td className="td text-right">{f.aimingMarkMm ? `${Math.round(f.aimingMarkMm)} mm` : "—"}</td>
                      <td className="td text-[12px] text-slate-600">{ringSummary(f.tr) || "—"}</td>
                      <td className="td text-center">
                        {f.f?.length ? (
                          <span className="rounded-md bg-success-soft px-2 py-0.5 text-[11px] font-bold text-success">Enabled</span>
                        ) : (
                          <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-muted">Disabled</span>
                        )}
                      </td>
                      <td className="td text-right">
                        <span className="inline-flex gap-1.5">
                          <button
                            className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-slate-50"
                            aria-label={`Edit ${f.name}`}
                            title="Edit"
                            onClick={() => setEditing(f)}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            className="rounded-lg border border-danger/40 p-2 text-danger transition-colors hover:bg-danger-soft"
                            aria-label={`Remove ${f.name}`}
                            title="Remove"
                            onClick={() => setPending([f])}
                          >
                            <Trash2 size={14} />
                          </button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TargetEditor
        face={editing}
        onClose={() => setEditing(null)}
        onSaved={(msg) => {
          setEditing(null);
          setNotice({ tone: "ok", text: msg });
          refresh();
        }}
      />

      <ConfirmDialog
        open={pending.length > 0}
        busy={busy}
        title={pending.length === 1 ? "Remove this target?" : `Remove ${pending.length} targets?`}
        intro="This takes the definition out of the stored target list."
        items={pending.map((f) => `${f.name}${f.distanceM ? ` — ${Math.round(f.distanceM)} m` : ""}`)}
        note={
          <>
            Results already recorded on {pending.length === 1 ? "this target" : "these targets"} keep the rings they were drawn
            with — every string holds its own copy. Only new imports are affected, and importing the definition file puts{" "}
            {pending.length === 1 ? "it" : "them"} back.
          </>
        }
        confirmLabel={pending.length === 1 ? "Remove target" : `Remove ${pending.length} targets`}
        onConfirm={remove}
        onCancel={() => setPending([])}
      />
    </div>
  );
}

/* ------------------------------- Add / edit -------------------------------- */
/*
 * One physical face carries TWO scoring layouts, and both are edited here:
 *
 *   Target Rifle — 5 down to 1, with V as the centre
 *   F-Class      — the same rings from 5 outwards, but the V ring counts 6 and a
 *                  smaller X sits inside it
 *
 * They are separate lists on the record, so the editor keeps them separate too.
 * Showing only one and saving that back would silently delete the other, and
 * every shooter in that class would then be drawn on the wrong rings.
 *
 * The board outline zones (the square "1" and "0" areas) are not editable —
 * they are not scoring circles — but they are carried through untouched rather
 * than dropped on save.
 */
type RingRow = { label: string; dia: string; centre: boolean };

const toRows = (rings: TargetFaceRing[] = []): RingRow[] =>
  rings
    .filter((r) => r.shape === "ring")
    .map((r) => ({
      label: r.label,
      dia: String(r.diameterMm ?? Math.round((r.radiusMm ?? 0) * 2)),
      centre: r.isCentre,
    }));

/* Back into the shape the target system uses, ready to store. */
const toZones = (rows: RingRow[], keep: TargetFaceRing[] = []) => {
  const rings = rows
    .filter((r) => r.label.trim() && Number(r.dia) > 0)
    .map((r) => ({
      name: r.label.trim(),
      // A centre ring scores as [points, centres] — a V counts 5, an X counts 6.
      score: r.centre ? [Number.isFinite(+r.label) ? +r.label : 5, 1] : Number(r.label) || 0,
      type: { name: "ring", dia: Number(r.dia) },
    }));
  // The board edges, preserved exactly as they came in.
  const boards = keep
    .filter((r) => r.shape === "rect")
    .map((r) => ({
      name: r.label,
      score: r.points,
      type: { name: "rect", width: r.widthMm, height: r.heightMm },
    }));
  return [...rings, ...boards];
};

function TargetEditor({
  face,
  onClose,
  onSaved,
}: {
  face: TargetFace | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const isNew = !face?.tr?.length && !face?.f?.length;
  const [name, setName] = useState("");
  const [distance, setDistance] = useState("");
  const [trRows, setTrRows] = useState<RingRow[]>([]);
  const [fRows, setFRows] = useState<RingRow[]>([]);
  const [tab, setTab] = useState<"tr" | "f">("tr");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // Load the chosen face into the form once per open.
  if (face && loadedFor !== face.name) {
    setLoadedFor(face.name);
    setName(face.name || "");
    setDistance(face.distanceM ? String(Math.round(face.distanceM)) : "");
    setTrRows(toRows(face.tr));
    setFRows(toRows(face.f));
    setTab("tr");
    setError(null);
  }
  if (!face) {
    if (loadedFor !== null) setLoadedFor(null);
    return null;
  }

  const rows = tab === "tr" ? trRows : fRows;
  const setRows = tab === "tr" ? setTrRows : setFRows;
  const setRow = (i: number, patch: Partial<RingRow>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  /* F-Class is the TR rings with the centre split — offered rather than typed. */
  const copyFromTr = () =>
    setFRows(() => {
      const base = trRows.filter((r) => r.label.trim() && Number(r.dia) > 0);
      if (!base.length) return [];
      const centre = base[base.length - 1];
      return [
        ...base.slice(0, -1),
        { label: "6", dia: centre.dia, centre: false },
        { label: "X", dia: String(Math.round((Number(centre.dia) / 2) * 10) / 10), centre: true },
      ];
    });

  const save = async () => {
    setError(null);
    if (!name.trim()) return setError("The target needs a name.");
    const metres = Number(distance);
    if (!metres || metres <= 0) return setError("Enter the distance in metres — the ring sizes in MOA depend on it.");
    const scoringzones = toZones(trRows, face.tr);
    const scoringzones_f = toZones(fRows, face.f);
    if (!scoringzones.length && !scoringzones_f.length) return setError("Add at least one scoring ring.");

    setSaving(true);
    try {
      const res = await apiPost<{ message: string }>("/admin/target-faces", {
        name: name.trim(),
        distance: metres,
        scoringzones,
        // Only sent when the face has one — an empty list would erase it.
        ...(scoringzones_f.length ? { scoringzones_f } : {}),
      });
      onSaved(res.message);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Add target" : `Edit ${face.name}`} wide>
      <div className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-[13px] font-semibold text-danger">
            <AlertCircle size={15} className="mt-px shrink-0" /> {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label">Target name</label>
            <input className="input" placeholder="AU-600y" value={name} onChange={(e) => setName(e.target.value)} />
            <p className="mt-1 text-xs text-muted">Must match the face name recorded in the shot log.</p>
          </div>
          <div>
            <label className="label">Distance (metres)</label>
            <input className="input" placeholder="548" value={distance} onChange={(e) => setDistance(e.target.value)} />
            <p className="mt-1 text-xs text-muted">600 yards = 548 m. Ring sizes in MOA are derived from this.</p>
          </div>
        </div>

        <div>
          {/* One face, two scoring layouts — kept apart so neither overwrites the other. */}
          <div className="flex flex-wrap items-center gap-2 border-b border-line pb-2">
            {([
              ["tr", "Target Rifle", trRows.length],
              ["f", "F-Class", fRows.length],
            ] as const).map(([key, label, count]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`rounded-lg px-3 py-1.5 text-[13px] font-bold transition-colors ${
                  tab === key ? "bg-primary-soft text-primary" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {label}
                <span className="ml-1.5 font-semibold text-muted">{count || "none"}</span>
              </button>
            ))}
            <span className="ml-auto text-xs text-muted">
              {tab === "tr" ? "Scores 5 → 1, centre counts as V" : "Scores 6 → 1, centre counts as X"}
            </span>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <label className="label mb-0">Scoring rings — diameter in millimetres</label>
            <div className="flex items-center gap-3">
              {tab === "f" && !fRows.length && trRows.length > 0 && (
                <button className="text-[13px] font-bold text-primary hover:underline" onClick={copyFromTr}>
                  Build from Target Rifle
                </button>
              )}
              <button
                className="text-[13px] font-bold text-primary hover:underline"
                onClick={() => setRows((r) => [...r, { label: "", dia: "", centre: false }])}
              >
                + Add ring
              </button>
            </div>
          </div>
          <p className="mb-2 mt-1 text-xs text-muted">
            Largest first. Tick <b>Centre</b> for the {tab === "tr" ? "V" : "X"} ring — it scores its value and counts as a
            centre.
          </p>
          <div className="slim-scrollbar max-h-64 space-y-2 overflow-y-auto pr-1">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  className="input w-20"
                  placeholder="5"
                  value={r.label}
                  onChange={(e) => setRow(i, { label: e.target.value })}
                  aria-label={`Ring ${i + 1} value`}
                />
                <input
                  className="input flex-1"
                  placeholder="290"
                  value={r.dia}
                  onChange={(e) => setRow(i, { dia: e.target.value })}
                  aria-label={`Ring ${i + 1} diameter in millimetres`}
                />
                <label className="flex shrink-0 items-center gap-1.5 text-[13px] font-semibold text-slate-600">
                  <input type="checkbox" checked={r.centre} onChange={(e) => setRow(i, { centre: e.target.checked })} />
                  Centre
                </label>
                <button
                  className="shrink-0 rounded-lg border border-line p-2 text-muted transition-colors hover:bg-slate-50"
                  onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                  aria-label={`Remove ring ${i + 1}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {!rows.length && (
              <p className="py-4 text-center text-[13px] text-muted">
                {tab === "f"
                  ? "This target has no F-Class layout. Leave it empty if F-Class is not shot on it."
                  : "No rings yet — add the outermost first."}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-line pt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Save target
          </Button>
        </div>
      </div>
    </Modal>
  );
}
